import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MailPlus, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/layout/PageHeader";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import StatusPill from "../components/status/StatusPill";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAuth } from "../lib/auth-context";
import { api, extractApiError } from "../lib/api";
import { formatDateTime } from "../lib/format";
import {
  canManageTenantTeam,
  getTeamRoleLabel,
  mapTeamInviteError,
  mapTeamMembersError,
} from "../lib/team";

const EMPTY_INVITE_FORM = {
  email: "",
  full_name: "",
  role: "VIEWER",
};

function statusTone(isActive) {
  return isActive === false ? "destructive" : "success";
}

function statusLabel(isActive) {
  return isActive === false ? "Inaktiv" : "Aktiv";
}

export default function TeamPage() {
  const { role, isSuperAdmin, tenant } = useAuth();
  const canManage = useMemo(
    () => canManageTenantTeam({ role, isSuperAdmin }),
    [role, isSuperAdmin],
  );

  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersError, setMembersError] = useState("");
  const [memberAction, setMemberAction] = useState({ id: null, type: null });
  const [memberRoleDrafts, setMemberRoleDrafts] = useState({});

  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeTargetMember, setRemoveTargetMember] = useState(null);
  const [removeError, setRemoveError] = useState("");

  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(true);
  const [invitesError, setInvitesError] = useState("");
  const [inviteAction, setInviteAction] = useState({ id: null, type: null });

  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revokeTargetId, setRevokeTargetId] = useState(null);
  const [revokeError, setRevokeError] = useState("");

  const [inviteForm, setInviteForm] = useState(EMPTY_INVITE_FORM);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteSuccess, setInviteSuccess] = useState("");

  const loadMembers = useCallback(async () => {
    if (!canManage) {
      setMembersLoading(false);
      setMembers([]);
      setMembersError("");
      return;
    }

    setMembersLoading(true);
    setMembersError("");
    try {
      const response = await api.get("/api/tenant/members");
      setMembers(response.data || []);
      setMemberRoleDrafts({});
    } catch (error) {
      setMembers([]);
      setMembersError(mapTeamMembersError(error?.response?.status));
    } finally {
      setMembersLoading(false);
    }
  }, [canManage]);

  const loadInvites = useCallback(async () => {
    if (!canManage) {
      setInvitesLoading(false);
      setInvites([]);
      setInvitesError("");
      return;
    }

    setInvitesLoading(true);
    setInvitesError("");
    try {
      const response = await api.get("/api/tenant/invites");
      setInvites(response.data || []);
    } catch (error) {
      setInvites([]);
      setInvitesError(mapTeamInvitesError(error?.response?.status));
    } finally {
      setInvitesLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    loadMembers();
    loadInvites();
  }, [loadMembers, loadInvites]);

  const reloadAll = useCallback(async () => {
    await Promise.all([loadMembers(), loadInvites()]);
  }, [loadMembers, loadInvites]);

  const handleResendInvite = async (inviteId) => {
    if (!canManage) return;

    setInviteAction({ id: inviteId, type: "resend" });
    try {
      await api.post(`/api/tenant/invites/${inviteId}/resend`);
      toast.success("Die Einladung wurde erneut versendet.");
      await loadInvites();
    } catch (error) {
      toast.error(mapTeamInviteActionError(error?.response?.status, "resend"));
    } finally {
      setInviteAction({ id: null, type: null });
    }
  };

  const handleMemberRoleChange = (membershipId, nextRole) => {
    setMemberRoleDrafts((current) => ({ ...current, [membershipId]: nextRole }));
  };

  const handleSaveMemberRole = async (membershipId) => {
    if (!canManage) return;
    const nextRole = memberRoleDrafts[membershipId];
    if (!nextRole) return;

    setMemberAction({ id: membershipId, type: "role" });
    try {
      await api.patch(`/api/tenant/members/${membershipId}`, { role: nextRole });
      toast.success("Die Rolle wurde gespeichert.");
      await loadMembers();
    } catch (error) {
      toast.error(mapTeamMemberActionError(error?.response?.status, "role"));
    } finally {
      setMemberAction({ id: null, type: null });
    }
  };

  const handleRemoveMember = (member) => {
    if (!canManage) return;
    setRemoveTargetMember(member);
    setRemoveError("");
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemoveMember = async () => {
    if (!canManage) return;
    if (!removeTargetMember?.membership_id) return;

    setRemoveError("");
    setMemberAction({ id: removeTargetMember.membership_id, type: "remove" });
    try {
      await api.delete(`/api/tenant/members/${removeTargetMember.membership_id}`);
      toast.success("Das Mitglied wurde entfernt.");
      await loadMembers();
      setRemoveDialogOpen(false);
      setRemoveTargetMember(null);
    } catch (error) {
      const message = mapTeamMemberActionError(error?.response?.status, "remove");
      setRemoveError(message);
      toast.error(message);
    } finally {
      setMemberAction({ id: null, type: null });
    }
  };

  const handleRevokeInvite = (inviteId) => {
    if (!canManage) return;
    setRevokeTargetId(inviteId);
    setRevokeError("");
    setRevokeDialogOpen(true);
  };

  const handleConfirmRevokeInvite = async () => {
    if (!canManage) return;
    if (!revokeTargetId) return;

    setRevokeError("");
    setInviteAction({ id: revokeTargetId, type: "revoke" });
    try {
      await api.delete(`/api/tenant/invites/${revokeTargetId}`);
      toast.success("Die Einladung wurde widerrufen.");
      await loadInvites();
      setRevokeDialogOpen(false);
      setRevokeTargetId(null);
    } catch (error) {
      const message = mapTeamInviteActionError(error?.response?.status, "revoke");
      setRevokeError(message);
      toast.error(message);
    } finally {
      setInviteAction({ id: null, type: null });
    }
  };

  const handleInviteChange = (key, value) => {
    setInviteForm((current) => ({ ...current, [key]: value }));
    if (inviteError) setInviteError("");
    if (inviteSuccess) setInviteSuccess("");
  };

  const handleInviteSubmit = async (event) => {
    event.preventDefault();
    if (!canManage) {
      setInviteError("Du hast keine Berechtigung, Nutzer einzuladen.");
      return;
    }

    setInviteSubmitting(true);
    setInviteError("");
    setInviteSuccess("");
    try {
      await api.post("/api/tenant/invites", {
        email: inviteForm.email,
        full_name: inviteForm.full_name,
        role: inviteForm.role,
      });
      setInviteForm(EMPTY_INVITE_FORM);
      setInviteSuccess("Die Einladung wurde versendet.");
      toast.success("Die Einladung wurde versendet.");
    } catch (error) {
      const status = error?.response?.status;
      const detail = status === 400 ? extractApiError(error, "") : "";
      const message = mapTeamInviteError(status, detail);
      setInviteError(message);
      toast.error(message);
    } finally {
      setInviteSubmitting(false);
    }
  };

  if (!canManage) {
    return (
      <div>
        <PageHeader
          title="Team verwalten"
          description={tenant?.name ? `Mandant: ${tenant.name}` : undefined}
        />
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Keine Berechtigung</AlertTitle>
          <AlertDescription>
            Du hast keine Berechtigung, dieses Team zu verwalten.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Team verwalten"
        description={tenant?.name ? `Mandant: ${tenant.name}` : "Verwalte Mitglieder und Einladungen deines Teams."}
        actions={(
          <Button
            variant="outline"
            onClick={reloadAll}
            disabled={membersLoading || invitesLoading || inviteSubmitting || inviteAction.id || memberAction.id}
          >
            <RefreshCw className="w-4 h-4" />
            {membersLoading || invitesLoading ? "Lädt …" : "Aktualisieren"}
          </Button>
        )}
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,0.9fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Mitglieder</CardTitle>
            <CardDescription>
              Alle Benutzer mit Zugriff auf den aktuell ausgewählten Mandanten.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {membersLoading ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                Mitglieder werden geladen …
              </div>
            ) : membersError ? (
              <Alert variant="destructive">
                <AlertTitle>Mitglieder konnten nicht geladen werden</AlertTitle>
                <AlertDescription>{membersError}</AlertDescription>
              </Alert>
            ) : members.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                Es sind noch keine Mitglieder vorhanden.
              </div>
            ) : (
              <div className="obsidian overflow-hidden rounded-lg border border-border">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-border bg-muted/40">
                      <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                        <th className="px-4 py-3 font-medium">Name</th>
                        <th className="px-4 py-3 font-medium">E-Mail</th>
                        <th className="px-4 py-3 font-medium">Rolle</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium">Mitglied seit</th>
                        <th className="px-4 py-3 font-medium text-right">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {members.map((member) => {
                        const draftRole = memberRoleDrafts[member.membership_id] ?? member.role;
                        const roleChanged = draftRole !== member.role;
                        const rowBusy = memberAction.id === member.membership_id;
                        return (
                          <tr key={member.membership_id}>
                          <td className="px-4 py-3 font-medium">{member.user?.full_name || "—"}</td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                            {member.user?.email || "—"}
                          </td>
                          <td className="px-4 py-3">
                            <Select
                              value={draftRole}
                              onValueChange={(value) => handleMemberRoleChange(member.membership_id, value)}
                              disabled={rowBusy}
                            >
                              <SelectTrigger className="h-8 w-[190px]">
                                <SelectValue placeholder="Rolle wählen" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="TENANT_ADMIN">Administrator</SelectItem>
                                <SelectItem value="SALES_USER">Vertrieb</SelectItem>
                                <SelectItem value="VIEWER">Betrachter</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3">
                            <StatusPill tone={statusTone(member.user?.is_active)}>
                              {statusLabel(member.user?.is_active)}
                            </StatusPill>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {formatDateTime(member.membership_created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={!roleChanged || rowBusy}
                                onClick={() => handleSaveMemberRole(member.membership_id)}
                              >
                                {rowBusy && memberAction.type === "role" ? "Speichert …" : "Speichern"}
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                disabled={rowBusy}
                                onClick={() => handleRemoveMember(member)}
                              >
                                {rowBusy && memberAction.type === "remove" ? "Entfernt …" : "Entfernen"}
                              </Button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Neuen Nutzer einladen</CardTitle>
            <CardDescription>
              Die Einladung wird per E-Mail an den neuen Nutzer versendet.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleInviteSubmit}>
              {inviteSuccess ? (
                <Alert>
                  <AlertTitle>Einladung versendet</AlertTitle>
                  <AlertDescription>{inviteSuccess}</AlertDescription>
                </Alert>
              ) : null}

              {inviteError ? (
                <Alert variant="destructive">
                  <AlertTitle>Einladung fehlgeschlagen</AlertTitle>
                  <AlertDescription>{inviteError}</AlertDescription>
                </Alert>
              ) : null}

              <Field label="E-Mail-Adresse">
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(event) => handleInviteChange("email", event.target.value)}
                  placeholder="user@example.com"
                  required
                  autoComplete="email"
                />
              </Field>

              <Field label="Vollständiger Name">
                <Input
                  type="text"
                  value={inviteForm.full_name}
                  onChange={(event) => handleInviteChange("full_name", event.target.value)}
                  placeholder="Max Mustermann"
                  required
                  autoComplete="name"
                />
              </Field>

              <Field label="Rolle">
                <Select
                  value={inviteForm.role}
                  onValueChange={(value) => handleInviteChange("role", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Rolle wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TENANT_ADMIN">Administrator</SelectItem>
                    <SelectItem value="SALES_USER">Vertrieb</SelectItem>
                    <SelectItem value="VIEWER">Betrachter</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Button
                type="submit"
                className="w-full"
                disabled={
                  inviteSubmitting ||
                  !inviteForm.email.trim() ||
                  !inviteForm.full_name.trim() ||
                  !inviteForm.role
                }
              >
                <MailPlus className="w-4 h-4" />
                {inviteSubmitting ? "Einladung wird gesendet …" : "Einladung senden"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Offene Einladungen</CardTitle>
          <CardDescription>
            Einladungen erscheinen hier, bis sie angenommen oder widerrufen wurden.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invitesLoading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Einladungen werden geladen …
            </div>
          ) : invitesError ? (
            <Alert variant="destructive">
              <AlertTitle>Einladungen konnten nicht geladen werden</AlertTitle>
              <AlertDescription>{invitesError}</AlertDescription>
            </Alert>
          ) : invites.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Es gibt aktuell keine offenen Einladungen.
            </div>
          ) : (
            <div className="obsidian overflow-hidden rounded-lg border border-border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-muted/40">
                    <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                      <th className="px-4 py-3 font-medium">Name</th>
                      <th className="px-4 py-3 font-medium">E-Mail</th>
                      <th className="px-4 py-3 font-medium">Rolle</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Ablauf</th>
                      <th className="px-4 py-3 font-medium text-right">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {invites.map((invite) => (
                      <tr key={invite.id}>
                        <td className="px-4 py-3 font-medium">{invite.full_name || "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                          {invite.invited_email || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill tone="info">{getTeamRoleLabel(invite.role)}</StatusPill>
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill tone={invite.status === "expired" ? "destructive" : "info"}>
                            {invite.status === "expired" ? "Abgelaufen" : "Offen"}
                          </StatusPill>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {formatDateTime(invite.expires_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={inviteAction.id === invite.id}
                              onClick={() => handleResendInvite(invite.id)}
                            >
                              Erneut senden
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              disabled={inviteAction.id === invite.id}
                              onClick={() => handleRevokeInvite(invite.id)}
                            >
                              Widerrufen
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={revokeDialogOpen}
        onOpenChange={(open) => {
          if (inviteAction.type === "revoke") return;
          setRevokeDialogOpen(open);
          if (!open) {
            setRevokeTargetId(null);
            setRevokeError("");
          }
        }}
      >
        <AlertDialogContent className="border-border/60 shadow-lg shadow-black/10 outline-none ring-0 ring-offset-0">
          <AlertDialogHeader>
            <AlertDialogTitle>Einladung widerrufen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Einladung kann danach nicht mehr verwendet werden. Bereits versendete Einladungslinks werden ungültig.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {revokeError ? (
            <Alert variant="destructive">
              <AlertTitle>Widerruf fehlgeschlagen</AlertTitle>
              <AlertDescription>{revokeError}</AlertDescription>
            </Alert>
          ) : null}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={inviteAction.type === "revoke"}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={inviteAction.type === "revoke"}
              onClick={(event) => {
                event.preventDefault();
                handleConfirmRevokeInvite();
              }}
            >
              {inviteAction.type === "revoke" ? "Widerrufen …" : "Einladung widerrufen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={removeDialogOpen}
        onOpenChange={(open) => {
          if (memberAction.type === "remove") return;
          setRemoveDialogOpen(open);
          if (!open) {
            setRemoveTargetMember(null);
            setRemoveError("");
          }
        }}
      >
        <AlertDialogContent className="border-border/60 shadow-lg shadow-black/10 outline-none ring-0 ring-offset-0">
          <AlertDialogHeader>
            <AlertDialogTitle>Mitglied entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              Dies entfernt nur die Mitgliedschaft in diesem Mandanten. Der Benutzer selbst wird nicht gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4">
            {removeError ? (
              <Alert variant="destructive">
                <AlertTitle>Entfernen fehlgeschlagen</AlertTitle>
                <AlertDescription>{removeError}</AlertDescription>
              </Alert>
            ) : null}

            <div className="grid grid-cols-1 gap-2 rounded-lg border border-border bg-muted/20 p-4 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Name</span>
                <span className="font-medium">{removeTargetMember?.user?.full_name || "-"}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">E-Mail</span>
                <span className="font-mono text-xs text-muted-foreground">{removeTargetMember?.user?.email || "-"}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Rolle</span>
                <StatusPill tone="info">{getTeamRoleLabel(removeTargetMember?.role)}</StatusPill>
              </div>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={memberAction.type === "remove"}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={memberAction.type === "remove"}
              onClick={(event) => {
                event.preventDefault();
                handleConfirmRemoveMember();
              }}
            >
              {memberAction.type === "remove" ? "Entfernen …" : "Entfernen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function mapTeamInvitesError(status) {
  if (status === 401) return "Bitte melde dich erneut an.";
  if (status === 403) return "Du hast keine Berechtigung, dieses Team zu verwalten.";
  return "Die Einladungen konnten nicht geladen werden.";
}

function mapTeamInviteActionError(status, action) {
  if (status === 401) return "Bitte melde dich erneut an.";
  if (status === 403) return "Du hast keine Berechtigung, Einladungen zu verwalten.";
  if (status === 404) return "Einladung nicht gefunden.";
  if (status === 409) {
    return action === "revoke"
      ? "Diese Einladung kann nicht widerrufen werden."
      : "Diese Einladung kann nicht erneut versendet werden.";
  }
  return action === "revoke"
    ? "Die Einladung konnte nicht widerrufen werden."
    : "Die Einladung konnte nicht erneut versendet werden.";
}

function mapTeamMemberActionError(status, action) {
  if (status === 401) return "Bitte melde dich erneut an.";
  if (status === 403) return "Du hast keine Berechtigung, dieses Team zu verwalten.";
  if (status === 404) return "Mitglied nicht gefunden.";
  if (status === 409) {
    return action === "remove"
      ? "Dieses Mitglied kann nicht entfernt werden."
      : "Die Rolle kann nicht geändert werden.";
  }
  return action === "remove" ? "Das Mitglied konnte nicht entfernt werden." : "Die Rolle konnte nicht geändert werden.";
}
